import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ActionButton from 'components/common/ActionButton';
import PageHeader from 'components/common/PageHeader';
import { useAppInsights } from '../../hooks/useAppInsights';
import parties from '../../config/parties';

const ListMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { trackPageView, trackError, trackCustomEvent } = useAppInsights();

  useEffect(() => {
    // Track page view
    trackPageView('MembersList');
    
    const fetchData = async () => {
      try {
        setError(null);
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/members/list`);
        setMembers(response.data);
        
        // Track successful data load
        trackCustomEvent('MembersListLoaded', {
          memberCount: response.data.length,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        setError(error.message);
        
        // Track error
        trackError(error, {
          component: 'ListMembers',
          operation: 'fetchMembers',
          apiUrl: `${process.env.REACT_APP_API_URL}/members/list`
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [trackPageView, trackCustomEvent, trackError]);

  const archiveMember = async (id, title) => {
    if (id != null && title) {
      if (window.confirm(`Are you sure you want to delete this member: ${title}?`)) {
        try {
          setMembers(members.filter((event) => event.id !== id));
          setError(null);
          await axios.delete(`${process.env.REACT_APP_API_URL}/members/archive/member/${id}`);
          
          // Track successful delete
          trackCustomEvent('MemberDeleted', {
            memberId: id,
            memberTitle: title,
            timestamp: new Date().toISOString()
          });
        } catch (error) {
          setError(error.message);
          
          // Track delete error
          trackError(error, {
            component: 'ListMembers',
            operation: 'deleteMember',
            memberId: id,
            memberTitle: title
          });
        }
      }
    }
  };

  function getPartyAbbreviation(partyId) {
    // Handle null, undefined, or empty partyId values
    if (!partyId) {
      return 'Unknown';
    }
    
    const party = parties.find((party) => party.value === partyId.toString());
    return party ? party.abbreviation : 'Unknown';
  }

  return (
    <>
      <PageHeader title="Members" description="" className="mb-3"></PageHeader>
      {loading ? (
        <div>Loading...</div>
      ) : error ? (
        <div>Error: {error}</div>
      ) : (
        <Table hover responsive>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Party</th>
              <th scope="col">Status</th>
              <th className="text-end" scope="col">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {members.length > 0 &&
              members.map((item, index) => (
                <tr key={index}>
                  <td>
                    <Link to={`/member/${item.id}`}>
                      {item.name} {item.surname}
                    </Link>
                  </td>
                  <td>{getPartyAbbreviation(item.party_id)}</td>
                  <td>{item.status}</td>
                  <td className="text-end">
                    <Link to={`/members/edit/${item.id}`}>
                      <ActionButton icon="edit" title="Edit" variant="action" className="p-0 me-2" /> Profile
                    </Link>
                    <Link to={`/members/editcommittees/${item.id}`}>
                      <ActionButton icon="edit" title="Edit" variant="action" className="p-0 me-2" /> Committees
                    </Link>
                    <Link to={`/members/editconstituencies/${item.id}`}>
                      <ActionButton icon="edit" title="Edit" variant="action" className="p-0 me-2" /> Constituencies
                    </Link>
                    <ActionButton
                      icon="trash-alt"
                      title="Delete"
                      variant="action"
                      className="p-0"
                      onClick={() => archiveMember(item.id, `${item.name} ${item.surname}`)}
                    />
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      )}
    </>
  );
};

export default ListMembers;
